import React from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Box, Text, Badge, IconButton, Flex, Spinner,
} from '@chakra-ui/react';
import { FiEye, FiExternalLink, FiAlertCircle } from 'react-icons/fi';
import dayjs from 'dayjs';
import { STATUS_LABELS, STATUS_COLOR } from 'api/administrador/monitoring/zoomRecordings';

const BADGE_STYLES = {
  green:  { bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
  blue:   { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  red:    { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
  orange: { bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
  gray:   { bg: '#F1F5F9', color: '#475569', border: '#E2E8F0' },
};

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || 'gray';
  const style = BADGE_STYLES[color];
  return (
    <Badge
      bg={style.bg} color={style.color}
      border="1px solid" borderColor={style.border}
      px={2.5} py={1} borderRadius="999px" fontSize="11px" fontWeight="600"
      textTransform="none"
    >
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

function fmtDuration(minutes) {
  const m = Number(minutes) || 0;
  if (m <= 0) return '';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
  return `${String(mm).padStart(2, '0')}:00`;
}

function Thumbnail({ log }) {
  // For OK: use thumbnail_url with play icon overlay + duration badge.
  // For error/pending: colored placeholder with icon.
  const isOk = log.status === 'recorded_class_created';
  const url = log.thumbnail_url;
  const duration = fmtDuration(log.duration_minutes);
  if (isOk && url) {
    return (
      <Box position="relative" w="96px" h="54px" borderRadius="8px" overflow="hidden" bg="gray.800" flexShrink={0}>
        <img src={url} alt="thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
        {/* Play icon overlay */}
        <Flex
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="24px"
          h="24px"
          borderRadius="999px"
          bg="rgba(255,255,255,.9)"
          align="center"
          justify="center"
          boxShadow="0 2px 6px rgba(0,0,0,.35)"
        >
          <Box
            w="0"
            h="0"
            borderLeft="8px solid #0F172A"
            borderTop="5px solid transparent"
            borderBottom="5px solid transparent"
            ml="2px"
          />
        </Flex>
        {/* Duration badge */}
        {duration && (
          <Box
            position="absolute"
            bottom="4px"
            right="4px"
            bg="rgba(0,0,0,.7)"
            color="white"
            fontSize="10px"
            px="5px"
            py="1px"
            borderRadius="3px"
            fontWeight="600"
            lineHeight={1.2}
          >
            {duration}
          </Box>
        )}
      </Box>
    );
  }
  // color background for pending/error state
  const bg = log.status === 'failed' ? 'linear-gradient(135deg, #7F1D1D, #B91C1C)'
    : log.status === 'unmatched' ? 'linear-gradient(135deg, #78350F, #D97706)'
    : 'linear-gradient(135deg, #1E293B, #334155)';
  return (
    <Flex w="96px" h="54px" borderRadius="8px" bg={bg} align="center" justify="center" color="white" flexShrink={0}>
      <FiAlertCircle size={20} />
    </Flex>
  );
}

export default function ZoomLogTable({ logs = [], loading, onViewDetail }) {
  if (loading) {
    return (
      <Flex justify="center" align="center" py={10}>
        <Spinner size="lg" color="#574feb" />
      </Flex>
    );
  }
  if (logs.length === 0) {
    return (
      <Box textAlign="center" py={10} color="gray.500">
        <Text>No hay grabaciones para los filtros seleccionados.</Text>
      </Box>
    );
  }
  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr bg="gray.50">
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>Grabación</Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>Cuenta(s)</Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>Fecha / hora</Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>Duración</Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>Estado</Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4} isNumeric></Th>
          </Tr>
        </Thead>
        <Tbody>
          {logs.map((log) => (
            <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
              <Td>
                <Flex gap={3} align="center">
                  <Thumbnail log={log} />
                  <Box minW={0}>
                    <Text fontWeight="600" fontSize="sm" isTruncated maxW="260px">
                      {log.topic || 'Sin tema'}
                    </Text>
                    <Text fontSize="xs" color="gray.500" isTruncated maxW="260px">
                      {log.live_class?.subject?.name || 'Sin materia'} · meeting {log.meeting_id}
                    </Text>
                  </Box>
                </Flex>
              </Td>
              <Td>
                <Text fontSize="sm" fontWeight="500">{log.host_email || '—'}</Text>
              </Td>
              <Td>
                <Text fontSize="sm">{log.start_time ? dayjs(log.start_time).format('DD MMM · HH:mm') : '—'}</Text>
              </Td>
              <Td>
                <Text fontSize="sm">{log.duration_minutes ? `${log.duration_minutes} min` : '—'}</Text>
              </Td>
              <Td>
                <StatusBadge status={log.status} />
              </Td>
              <Td isNumeric>
                <Flex gap={1} justify="flex-end">
                  <IconButton
                    aria-label="Ver detalle"
                    size="sm" variant="outline"
                    icon={<FiEye />}
                    onClick={() => onViewDetail(log)}
                  />
                  {log.recorded_class?.video && (
                    <IconButton
                      aria-label="Abrir video"
                      size="sm" variant="outline"
                      icon={<FiExternalLink />}
                      as="a" href={log.recorded_class.video} target="_blank" rel="noopener noreferrer"
                    />
                  )}
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
